import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, forwardRef, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { extractErrors } from '@vsolv/dev-kit/ngx';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { BehaviorSubject, map, takeUntil } from 'rxjs';
import { createPropertyControl } from '../properties';
import { StaticPropertyFormComponent } from '../static-property-form.component';
import { buildVinPatch, DecodedVehicle, decodeVin } from './vin-decode';

export type { VehicleResponse } from './vin-decode';

type VehicleAssignment = Property.ObjectPropertyAssignment & {
  property: Property.Model<Property.PropertyType>;
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-vehicle-property-input',
  templateUrl: './vehicle-property-input.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => VehiclePropertyInputComponent),
    },
    { provide: NG_VALIDATORS, useExisting: VehiclePropertyInputComponent, multi: true },
  ],
})
export class VehiclePropertyInputComponent extends StaticPropertyFormComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private toastSvc: ToastService
  ) {
    super();
  }
  vehicleCheckbox = new FormControl(false);

  overlayOpen = false;
  expanded = false;
  detailsExpanded = false;

  autofillSearch$ = new BehaviorSubject<string>('');

  override property!: Property.ObjectModel;

  assignments: VehicleAssignment[] = [];
  visibleExpandedAssignments: VehicleAssignment[] = [];
  fieldRoles: Partial<Record<PropertySet.VehicleFieldRole, VehicleAssignment>> = {};

  form = this.fb.group({}) as FormGroup<Record<string, FormControl>>;

  vehicle$ = new BehaviorSubject<DecodedVehicle | null>(null);

  formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));

  async useVIN(vin: string) {
    const results = await decodeVin(this.http, this.toastSvc, vin);
    if (!results) return;

    this.vehicle$.next(results);
    this.form.patchValue(
      buildVinPatch(
        {
          vin: this.fieldRoles.vin?.property.valueKey,
          make: this.fieldRoles.make?.property.valueKey,
          model: this.fieldRoles.model?.property.valueKey,
          year: this.fieldRoles.year?.property.valueKey,
          trim: this.fieldRoles.trim?.property.valueKey,
        },
        results,
        vin
      )
    );
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.expanded = !this.expanded;
  }

  get vinAssignment(): VehicleAssignment | undefined {
    return this.fieldRoles.vin;
  }

  get vinControl(): FormControl | null {
    const assignment = this.vinAssignment;
    if (!assignment) return null;

    return this.form.get(assignment.property.valueKey) as FormControl | null;
  }

  getSummaryValue(role: Exclude<PropertySet.VehicleFieldRole, 'vin'>): string | number | null {
    const assignment = this.fieldRoles[role];
    if (!assignment) return null;

    return this.form.get(assignment.property.valueKey)?.value ?? null;
  }

  override writeValue(value: unknown): void {
    super.writeValue(value);

    if (value && typeof value === 'object') {
      this.form.patchValue(value as Partial<typeof this.form.value>, { emitEvent: false });
      this.syncValidityAfterValueLoad();
    }
  }

  /**
   * Nested required children (e.g. a newly added Manufacturer field) live on `this.form`.
   * The outer `formControl` only has Validators.required on the object itself, so without
   * this override an incomplete existing vehicle still looks valid.
   */
  override validate(): ValidationErrors | null {
    if (this.form) {
      const formErrors = extractErrors(this.form);
      if (formErrors) {
        this.valid = false;
        return formErrors;
      }
    }
    return super.validate();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.assignments = this.getAssignments();
    this.fieldRoles = this.resolveFieldRoles(this.assignments);
    this.visibleExpandedAssignments = this.assignments.filter(assignment => !this.isVinAssignment(assignment));
    this.form = this.buildForm(this.assignments);
    this.formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));

    const existingValue = this.formControl?.value;
    if (existingValue && typeof existingValue === 'object') {
      this.form.patchValue(existingValue as Record<string, unknown>, { emitEvent: false });
    }

    this.syncValidityAfterValueLoad();

    const customerVehicle =
      this.extraData && this.matchesAssignedField(this.extraData['vehicle'], this.fieldRoles.make)
        ? this.extraData['vehicle']
        : undefined;
    if (customerVehicle) {
      this.formControl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(value => {
        this.vehicleCheckbox.setValue(JSON.stringify(customerVehicle) === JSON.stringify(value));
      });
    }

    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      // Keep partial values so the user can finish filling newly required fields.
      this.formControl.setValue(this.form.value);
    });
  }

  /** Expand + surface required errors when loaded values miss newly required children. */
  private syncValidityAfterValueLoad() {
    if (!this.form || this.form.valid) return;

    this.expanded = true;
    this.form.markAllAsTouched();
    this.formControl.updateValueAndValidity({ emitEvent: false });
  }

  private getAssignments(): VehicleAssignment[] {
    return (this.property.properties ?? [])
      .filter((assignment: Property.ObjectPropertyAssignment): assignment is VehicleAssignment =>
        Boolean(assignment.property)
      )
      .sort((a: VehicleAssignment, b: VehicleAssignment) => a.order - b.order);
  }

  private buildForm(assignments: VehicleAssignment[]): FormGroup<Record<string, FormControl>> {
    const controls = assignments.reduce<Record<string, FormControl>>((current, assignment) => {
      const control = createPropertyControl(assignment.property, assignment.required);

      if (this.fieldRoles.year?.property.valueKey === assignment.property.valueKey) {
        control.addValidators([Validators.pattern('^[0-9]*$'), Validators.minLength(4), Validators.maxLength(4)]);
      }

      current[assignment.property.valueKey] = control;
      return current;
    }, {});

    return new FormGroup(controls);
  }

  private resolveFieldRoles(
    assignments: VehicleAssignment[]
  ): Partial<Record<PropertySet.VehicleFieldRole, VehicleAssignment>> {
    return assignments.reduce<Partial<Record<PropertySet.VehicleFieldRole, VehicleAssignment>>>((roles, assignment) => {
      const role = this.resolveAssignmentRole(assignment);
      if (role && !roles[role]) roles[role] = assignment;

      return roles;
    }, {});
  }

  private resolveAssignmentRole(assignment: VehicleAssignment): PropertySet.VehicleFieldRole | null {
    return PropertySet.getVehicleFieldRole([
      assignment.displayLabel,
      assignment.property.name,
      assignment.property.valueKey,
    ]);
  }

  private isVinAssignment(assignment: VehicleAssignment): boolean {
    return this.fieldRoles.vin?.property.valueKey === assignment.property.valueKey;
  }

  private matchesAssignedField(value: unknown, assignment: VehicleAssignment | undefined): boolean {
    if (!assignment || !value || typeof value !== 'object') return false;

    return Boolean((value as Record<string, unknown>)[assignment.property.valueKey]);
  }
}
